<template>
  <div class="offcanvas-body">
    <p class="text-paragraph">
      Compartilhe qualquer horário específico com seu paciente para agendar uma
      sessão na sua agenda.
    </p>

    <TextInput
      required
      label="Data da sessão"
      :model="dateModel"
      type="date"
      placeholder="01/01/2000"
    />
    <TextInput
      required
      label="Horário de inicio da sessão"
      :model="timeModel"
      type="time"
      placeholder="08:00"
    />
    <MoneyInput
      label="Valor da sessão (opcional)"
      :model="valueModel"
      :placeholder="
        session?.appointment_price
          ? `Padrão: R$ ${session.appointment_price}`
          : 'Deixe vazio para usar o valor padrão'
      "
      :errorMsg="valueModel.$errors[0]?.$message"
    />

    <FormButton label="Gerar link" @onClick="submit" :loading="submitting" />
    <p v-if="errorMessage" class="text-danger text-center">
      {{ errorMessage }}
    </p>

    <div class="card mt-4" v-if="link">
      <div class="card-header">
        <p class="text-header mb-0">Link gerado</p>
      </div>
      <div class="card-body">
        <p class="text-paragraph">
          {{ link }}
        </p>
        <div class="d-flex gap-2">
          <button
            class="btn btn-sm"
            @click="copyLink"
            :class="{
              'btn-outline-primary': !linkCopied,
              'btn-success': linkCopied,
            }"
            :disabled="linkCopied"
          >
            <span v-if="linkCopied">
              <i class="fa-solid fa-check-circle"></i> Copiado
            </span>
            <span v-else> <i class="fas fa-copy"></i> Copiar </span>
          </button>
          <a
            class="btn btn-sm btn-outline-success"
            :href="whatsappShareLink"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i class="fab fa-whatsapp"></i> Compartilhar via WhatsApp
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useVeeField } from "@/composables/useVeeField";
import { sharedAppointmentSlotRepo } from "@/di/repositories";
import { shareScheduleSchema } from "@/schemas/shareSchedule";
import { useSessionStore } from "@/store/useSessionStore";
import { EventBus, EventBusNames } from "@/utils/EventBus";
import { toTypedSchema } from "@vee-validate/zod";
import { unformat } from "v-money3";
import { useForm } from "vee-validate";
import { computed, ref } from "vue";

const moneyConfig = {
  prefix: "R$ ",
  thousands: "",
  decimal: ",",
  precision: 2,
};

const sessionStore = useSessionStore();
const session = computed(() => sessionStore.session);

const submitting = ref(false);
const link = ref("");
const linkCopied = ref(false);
const errorMessage = ref("");
// const formKey = ref(0);

const { handleSubmit, resetForm, setErrors } = useForm({
  validationSchema: toTypedSchema(shareScheduleSchema),
  initialValues: {
    date: "",
    time: "",
    value: session.value?.appointment_price,
  },
});

const dateModel = useVeeField("date");
const timeModel = useVeeField("time");
const valueModel = useVeeField("value");

const whatsappShareLink = computed(() => {
  const message = encodeURIComponent(
    `Olá! Segue o link para agendar sua sessão comigo: ${link.value}`,
  );
  return `https://wa.me/?text=${message}`;
});

const submit = handleSubmit(async (formValues) => {
  try {
    errorMessage.value = "";
    submitting.value = true;
    const slot = await sharedAppointmentSlotRepo.createSharedAppointmentSlot({
      date: `${formValues.date} ${formValues.time}:00`,
      value: formValues.value
        ? (unformat(String(formValues.value), moneyConfig) as number)
        : undefined,
    });
    link.value = slot.share_url;
    // resetForm();
    EventBus.$emit(EventBusNames.SHOW_TOAST, {
      title: "Link gerado",
      msg: "Um link com seu horário avulso foi gerado para você poder copiar e compartilhar",
    });
  } catch (error: any) {
    console.error(error);
    errorMessage.value = error?.message || "Não foi possível gerar o link";
  } finally {
    submitting.value = false;
  }
});

function copyLink() {
  if (link.value) {
    linkCopied.value = true;
    setTimeout(() => {
      linkCopied.value = false;
    }, 3000);
    navigator.clipboard.writeText(link.value);
  }
}
</script>
